import type { ReactNode } from 'react';
import { useDelayedFlag } from '#client/hooks/useDelayedFlag';
import { PageLoading, PageMessage } from './PageStatus';

interface RecordPageProps<T> {
  /** Heading for the loading and not-found states, before the record can name itself. */
  title: string;
  backAction?: ReactNode | undefined;
  record: T | undefined;
  loading: boolean;
  error?: string | undefined;
  /** Shown when the request settled but produced nothing, e.g. "Lab test not found". */
  missingMessage: string;
  fullWidth?: boolean | undefined;
  /** Rendered only with a real record, so it can supply its own `<Page>`. */
  children: (record: T) => ReactNode;
}

/**
 * The load / fail / missing branch that precedes every single-record screen.
 *
 * One component, so no screen can drift — lose its back button while loading,
 * or word "not found" its own way.
 *
 * Deliberately not the same component as `ResourceFormShell`: an absent record
 * means "not found" here, but on a create form it means "there is nothing to
 * load yet", which is a normal state rather than an error.
 */
export function RecordPage<T>({
  title,
  backAction,
  record,
  loading,
  error,
  missingMessage,
  fullWidth,
  children,
}: RecordPageProps<T>) {
  // A placeholder that appears and vanishes inside 200ms reads as a flicker, so
  // a fast response renders nothing at all rather than a strobe.
  const showLoading = useDelayedFlag(loading);

  if (loading) {
    return showLoading ? (
      <PageLoading title={title} backAction={backAction} fullWidth={fullWidth} />
    ) : null;
  }

  if (error !== undefined || record === undefined) {
    return (
      <PageMessage title={title} backAction={backAction} fullWidth={fullWidth}>
        {error ?? missingMessage}
      </PageMessage>
    );
  }

  return <>{children(record)}</>;
}
